import { AuthData, authManager } from 'lib-shared-auth';
import { AsyncStatus } from 'lib-shared-types-api';
import { useCallback } from 'react';

import { AppLogInType } from 'app/types/app.types';

import { authResolverErrorFallThroughValue } from '../../useAuthDataResolver.constants';

/** The result of the trigger callback. */
export type UseAuthLocalStorageTriggerResult = {
  /** The type of the login. */
  readonly type: Extract<AppLogInType, 'localStorage'>;
  /** The async status. */
  readonly asyncStatus: Extract<AsyncStatus, 'fulfilled'>;
  /** The auth data. */
  readonly authData: AuthData;
};

/** Represents the trigger for getting the auth data. */
type Trigger = (
  prevError: Error | unknown,
) => Promise<UseAuthLocalStorageTriggerResult>;

/** A hook that resolves the auth data from the local storage. */
export const useAuthLocalStorage = (): Trigger => {
  const getAuthDataFromLocalStorage = useCallback(
    async (prevError: Error | unknown) => {
      if (
        (typeof prevError === 'string' || prevError instanceof Error) &&
        prevError !== authResolverErrorFallThroughValue
      ) {
        return Promise.reject(prevError);
      }
      const authData = await authManager.getAuthData({
        localStorageOnly: true,
      });

      return authData && authManager.validateAuthDataComplete(authData)
        ? Promise.resolve<UseAuthLocalStorageTriggerResult>({
            type: 'localStorage',
            asyncStatus: 'fulfilled',
            authData,
          })
        : Promise.reject(authResolverErrorFallThroughValue);
    },
    [],
  );

  /** Triggers the retrieval of the auth data in a promise. */
  const trigger = useCallback(
    (prevError: Error | unknown) => {
      return getAuthDataFromLocalStorage(prevError);
    },
    [getAuthDataFromLocalStorage],
  );

  return trigger;
};
